import type { ComponentProps, ReactNode } from "react";
import { cx } from "../util";
import { Spinner } from "./Spinner";
import { Tooltip } from "./Tooltip";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

/**
 * Class string for anything that should LOOK like a button (e.g. a router
 * `<Link>`): `<Link className={buttonClass({ variant: "secondary" })} to="/x">`.
 */
export function buttonClass(opts: { variant?: ButtonVariant; size?: ButtonSize; block?: boolean; className?: string } = {}): string {
  const { variant = "secondary", size = "md", block, className } = opts;
  return cx("gf-btn", `gf-btn--${variant}`, `gf-btn--${size}`, block && "gf-btn--block", className);
}

export interface ButtonProps extends Omit<ComponentProps<"button">, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, keeps the width, and disables the button. */
  loading?: boolean;
  /** Leading icon (e.g. `<Plus size={16} />`). */
  icon?: ReactNode;
  /** Trailing icon. */
  iconRight?: ReactNode;
  /** Full width. */
  block?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  icon,
  iconRight,
  block,
  className,
  disabled,
  type = "button",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(buttonClass({ variant, size, block }), loading && "gf-btn--loading", className)}
    >
      {loading ? (
        <span className="gf-btn__spinner">
          <Spinner size={size === "sm" ? 14 : 16} label={null} />
        </span>
      ) : null}
      {icon ? <span className="gf-btn__icon">{icon}</span> : null}
      {children !== undefined && children !== null ? <span className="gf-btn__label">{children}</span> : null}
      {iconRight ? <span className="gf-btn__icon">{iconRight}</span> : null}
    </button>
  );
}

export interface IconButtonProps extends Omit<ComponentProps<"button">, "children"> {
  /** Accessible name; also shown as a tooltip unless `tooltip={false}`. */
  label: string;
  icon: ReactNode;
  variant?: "ghost" | "secondary" | "primary" | "danger";
  size?: ButtonSize;
  tooltip?: boolean;
  loading?: boolean;
}

export function IconButton({
  label,
  icon,
  variant = "ghost",
  size = "md",
  tooltip = true,
  loading,
  className,
  type = "button",
  disabled,
  ...rest
}: IconButtonProps) {
  const btn = (
    <button
      {...rest}
      type={type}
      aria-label={label}
      disabled={disabled || loading}
      className={cx("gf-btn", `gf-btn--${variant}`, `gf-iconbtn`, `gf-iconbtn--${size}`, className)}
    >
      {loading ? <Spinner size={size === "sm" ? 14 : 16} label={null} /> : icon}
    </button>
  );
  return tooltip ? (
    <Tooltip content={label} describeChild={false}>
      {btn}
    </Tooltip>
  ) : (
    btn
  );
}
